import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/auth';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useCollectionData } from 'react-firebase-hooks/firestore';

firebase.initializeApp({
  apiKey: 'AIzaSyB1boAlu4hsdUAu90Vgys6juJjwz6yqa_s',
  authDomain: 'social-strangers.firebaseapp.com',
  projectId: 'social-strangers',
  storageBucket: 'social-strangers.appspot.com',
  messagingSenderId: '844617729848',
  appId: '1:844617729848:web:42c6756cabe5aa417ee3cd',
  measurementId: 'G-5Z2Q7TJMD1'
});

const auth = firebase.auth();
const firestore = firebase.firestore();

function Icon({ name, size = 20, ...props }) {
  const paths = {
    chat: <><path d="M20 11.5a7.5 7.5 0 0 1-7.5 7.5H6l-4 3v-6A7.5 7.5 0 0 1 9.5 4H12" /><path d="M14 2h3.5A4.5 4.5 0 0 1 22 6.5V12l-3-2h-4.5A4.5 4.5 0 0 1 10 5.5V5" /></>,
    search: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" /></>,
    info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v6M12 7h.01" /></>,
    arrow: <><path d="M12 19V5m-6 6 6-6 6 6" /></>,
    close: <path d="m6 6 12 12M6 18 18 6" />,
    exit: <><path d="M9 4H5v16h4m5-12 4 4-4 4M9 12h10" /></>,
    globe: <><circle cx="12" cy="12" r="9" /><ellipse cx="12" cy="12" rx="4" ry="9" /><path d="M3 12h18" /></>,
    chevron: <path d="m9 5 7 7-7 7" />,
    down: <path d="m6 9 6 6 6-6" />,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>{paths[name]}</svg>;
}

function Brand({ compact = false }) {
  return <div className={`brand${compact ? ' brand-compact' : ''}`}><span className="brand-mark"><Icon name="chat" size={21} /></span><span>strangers<span className="brand-period">.</span></span></div>;
}

function firstName(displayName) {
  return (typeof displayName === 'string' ? displayName.trim().split(/\s+/)[0] : '') || 'Guest';
}

function Avatar({ person, className = '' }) {
  const [failed, setFailed] = useState(false);
  const name = firstName(person.displayName);
  const initials = Array.from(name)[0].toUpperCase();
  useEffect(() => setFailed(false), [person.photoURL]);
  return <span className={`avatar ${className}`}>
    {person.photoURL && !failed
      ? <img src={person.photoURL} alt={name} referrerPolicy="no-referrer" onError={() => setFailed(true)} />
      : <span role="img" aria-label={name}>{initials}</span>}
  </span>;
}

function App() {
  const [user, loading] = useAuthState(auth);
  return <div className="App">
    <div className="page-masthead"><Brand compact /><span>A little more perspective.</span></div>
    {loading ? <div className="auth-loading" role="status"><span className="loading-dot" />Opening your space…</div>
      : user ? <ChatRoom user={user} /> : <SignIn />}
    <div className="page-footer"><span>Different people. Shared curiosity.</span><span>Strangers © {new Date().getFullYear()}</span></div>
  </div>;
}

function GoogleIcon() {
  return <svg className="google-icon" width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#4285F4" d="M43.6 24.5c0-1.4-.1-2.8-.4-4.1H24v7.8h11a9.4 9.4 0 0 1-4.1 6.2v5.2h6.7c3.9-3.6 6-8.9 6-15.1Z" /><path fill="#34A853" d="M24 44c5.5 0 10.1-1.8 13.5-4.9l-6.7-5.2c-1.8 1.2-4.1 1.9-6.8 1.9-5.3 0-9.8-3.6-11.4-8.3H5.7v5.4A20.4 20.4 0 0 0 24 44Z" /><path fill="#FBBC05" d="M12.6 27.5a12 12 0 0 1 0-7.5v-5.4H5.7a20.3 20.3 0 0 0 0 18.3l6.9-5.4Z" /><path fill="#EA4335" d="M24 11.8c3 0 5.6 1 7.7 3l5.8-5.8A19.6 19.6 0 0 0 24 4 20.4 20.4 0 0 0 5.7 14.6l6.9 5.4C14.2 15.3 18.7 11.8 24 11.8Z" /></svg>;
}

function SignIn() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const signIn = async () => {
    setPending(true);
    setError('');
    try {
      await auth.signInWithPopup(new firebase.auth.GoogleAuthProvider());
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') setError('We couldn’t sign you in. Please try again.');
    } finally { setPending(false); }
  };
  return <main className="welcome-shell">
    <div className="welcome-story">
      <div className="story-eyebrow"><span /> ROOM FOR A NEW PERSPECTIVE</div>
      <h1>Good conversations.<br /><span>Unexpected connections.</span></h1>
      <p>A small space to share what’s on your mind.<br className="desktop-break" /> A chance to see things a little differently.</p>
      <div className="conversation-art" aria-hidden="true">
        <div className="art-bubble art-bubble-one"><span /><span /><span /></div>
        <div className="art-bubble art-bubble-two"><Icon name="chat" size={42} /></div>
        <span className="art-caption">A simple hello can go a long way.</span>
      </div>
      <div className="story-footnote"><Icon name="globe" size={17} /><span>Different backgrounds. One shared space.</span></div>
    </div>
    <section className="welcome-panel" aria-labelledby="welcome-title">
      <div className="welcome-content">
        <div className="welcome-symbol"><Icon name="chat" size={29} /></div>
        <span className="eyebrow">MAKE YOURSELF AT HOME</span>
        <h2 id="welcome-title">Welcome to Strangers.</h2>
        <p>There’s always another point of view.<br />Come find yours in the conversation.</p>
        <button className="sign-in" onClick={signIn} disabled={pending}><GoogleIcon />{pending ? 'Connecting…' : 'Sign in with Google'}<Icon name="chevron" size={16} /></button>
        {error && <p className="sign-in-error" role="alert">{error}</p>}
        <div className="welcome-note">Bring your curiosity.<br />A little kindness goes a long way.</div>
      </div>
      <div className="welcome-panel-footer">ONE CONVERSATION AT A TIME</div>
    </section>
  </main>;
}

function ChatRoom({ user }) {
  const demoQuery = useMemo(() => firestore.collection('demoMessages').orderBy('createdAt', 'asc').limit(10000), []);
  const liveMessagesRef = useMemo(() => firestore.collection('conversationMessages'), []);
  const liveQuery = useMemo(() => liveMessagesRef.orderBy('createdAt', 'asc').limitToLast(1000), [liveMessagesRef]);
  const [demoMessages, demoLoading, demoError] = useCollectionData(demoQuery, { idField: 'id' });
  const [liveMessages, liveLoading, liveError] = useCollectionData(liveQuery, { idField: 'id' });
  const messages = useMemo(() => [
    ...(demoMessages || []).map(message => ({ ...message, id: `demo:${message.id}` })),
    ...(liveMessages || []).map(message => ({ ...message, id: `live:${message.id}` })),
  ].sort((a, b) => {
    // Unresolved server timestamps belong at the end until Firebase confirms them.
    const aTime = messageDate(a)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const bTime = messageDate(b)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    return aTime - bTime || a.id.localeCompare(b.id);
  }), [demoMessages, liveMessages]);

  const saveMessage = text => liveMessagesRef.add({
    text,
    uid: user.uid,
    displayName: (user.displayName || 'Guest').slice(0, 100),
    photoURL: user.photoURL || '',
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
  });

  return <ChatWorkspace key={user.uid} user={user} messages={messages} loading={demoLoading || liveLoading} error={demoError || liveError} onSendMessage={saveMessage} onSignOut={() => auth.signOut()} />;
}

function messageDate(message) {
  if (!message.createdAt) return null;
  const date = typeof message.createdAt.toDate === 'function' ? message.createdAt.toDate()
    : typeof message.createdAt.seconds === 'number' ? new Date(message.createdAt.seconds * 1000) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

function dateLabel(date) {
  if (!date) return '';
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', ...(date.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}) });
}

function RoomInformation({ onClose }) {
  const close = useRef();
  useEffect(() => {
    const previous = document.activeElement;
    close.current.focus();
    return () => previous?.focus();
  }, []);
  const onKeyDown = event => {
    if (event.key === 'Escape') onClose();
    // The close button is the dialog's only focusable control.
    if (event.key === 'Tab') { event.preventDefault(); close.current.focus(); }
  };
  return <div className="dialog-backdrop" onClick={onClose}>
    <section className="room-dialog" role="dialog" aria-modal="true" aria-labelledby="room-title" onClick={e => e.stopPropagation()} onKeyDown={onKeyDown}>
      <button ref={close} className="icon-button dialog-close" aria-label="Close information" onClick={onClose}><Icon name="close" /></button>
      <span className="room-symbol"><Icon name="chat" size={26} /></span>
      <span className="eyebrow">A SHARED SPACE</span>
      <h2 id="room-title">About the commons</h2>
      <p>A place to exchange ideas, share something interesting, and find a new perspective.</p>
      <div className="room-detail"><strong>Lead with curiosity.</strong><span>Thoughtful questions make better conversations.</span></div>
      <div className="room-detail"><strong>Keep it considerate.</strong><span>There’s a real person on the other side.</span></div>
      <div className="room-detail"><strong>One shared conversation.</strong><span>Messages are saved and visible to everyone signed in to this space.</span></div>
    </section>
  </div>;
}

export function ChatWorkspace({ user, messages, loading, error, onSignOut, onSendMessage }) {
  const scroller = useRef();
  const input = useRef();
  const searchInput = useRef();
  const searchToggle = useRef();
  const nearBottom = useRef(true);
  const hasLoaded = useRef(false);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const wasSending = useRef(false);
  const [sendError, setSendError] = useState('');
  const [formValue, setFormValue] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [informationOpen, setInformationOpen] = useState(false);
  const [showLatest, setShowLatest] = useState(false);
  const [accountError, setAccountError] = useState('');
  const allMessages = !error && messages ? messages : [];
  const term = search.trim().toLocaleLowerCase();
  const visibleMessages = term ? allMessages.filter(message => `${message.text} ${message.displayName || ''}`.toLocaleLowerCase().includes(term)) : allMessages;
  const participantCount = new Set(allMessages.map(message => message.uid).filter(Boolean)).size;

  const scrollToLatest = useCallback(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
    nearBottom.current = true;
    setShowLatest(false);
  }, []);
  useEffect(() => {
    if (!loading && !hasLoaded.current && messages) { scrollToLatest(); hasLoaded.current = true; }
    else if (nearBottom.current && !term) scrollToLatest();
  }, [messages, loading, term, scrollToLatest]);
  useEffect(() => { if (searchOpen) searchInput.current?.focus(); }, [searchOpen]);
  useEffect(() => {
    const area = input.current;
    if (area) { area.style.height = 'auto'; area.style.height = `${Math.min(area.scrollHeight, 132)}px`; }
  }, [formValue]);

  const sendMessage = async event => {
    event.preventDefault();
    const text = formValue.trim();
    if (!text || text.length > 4000 || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setSendError('');
    try {
      await onSendMessage(text);
      nearBottom.current = true;
      setFormValue('');
      setSearch('');
      setSearchOpen(false);
      scrollToLatest();
    } catch (err) {
      setSendError(err.code === 'permission-denied'
        ? 'Your message couldn’t be saved. Your account may not have permission to send here.'
        : 'Your message couldn’t be saved. Please try sending again.');
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };
  useEffect(() => {
    if (wasSending.current && !sending) input.current?.focus();
    wasSending.current = sending;
  }, [sending]);
  const closeSearch = () => { setSearchOpen(false); setSearch(''); searchToggle.current?.focus(); };
  const signOut = async () => {
    try { await onSignOut(); } catch (err) { setAccountError('Unable to sign out. Please try again.'); }
  };

  return <div className="workspace">
    <aside className="sidebar" aria-label="About Strangers">
      <Brand />
      <div className="sidebar-about"><span className="eyebrow">A SHARED SPACE</span><h2>One shared<br />conversation.</h2><p>Everyone meets here.<br />Bring a different point of view.</p></div>
      <div className="account"><Avatar person={user} /><div className="account-copy"><strong>{firstName(user.displayName)}</strong><span>Your space to connect</span></div><button className="icon-button" aria-label="Sign out" title="Sign out" onClick={signOut}><Icon name="exit" size={18} /></button></div>
      {accountError && <p className="account-error" role="alert">{accountError}</p>}
    </aside>
    <section className="conversation" aria-label="The commons conversation">
      <header className="conversation-header">
        <span className="room-symbol mobile-room-symbol"><Icon name="chat" size={22} /></span>
        <div className="conversation-heading"><h1>The commons</h1><p>{participantCount ? `${participantCount} ${participantCount === 1 ? 'voice' : 'voices'} in the conversation` : 'A space for good conversation'}</p></div>
        <div className="header-actions"><button ref={searchToggle} className={`icon-button${searchOpen ? ' is-active' : ''}`} aria-label="Search conversation" aria-expanded={searchOpen} onClick={() => { if (searchOpen) closeSearch(); else setSearchOpen(true); }}><Icon name="search" /></button><button className="icon-button" aria-label="Conversation information" title="Conversation information" onClick={() => setInformationOpen(true)}><Icon name="info" /></button><button className="icon-button mobile-sign-out" aria-label="Sign out" title="Sign out" onClick={signOut}><Icon name="exit" /></button></div>
      </header>
      {accountError && <p className="mobile-account-error" role="alert">{accountError}</p>}
      {searchOpen && <div className="search-bar"><Icon name="search" size={18} /><input ref={searchInput} aria-label="Search messages" value={search} placeholder="Search this conversation" onChange={e => setSearch(e.target.value)} onKeyDown={e => { if (e.key === 'Escape') closeSearch(); }} /><span className="search-count" role="status">{term ? `${visibleMessages.length} found` : ''}</span><button className="icon-button" aria-label="Close search" onClick={closeSearch}><Icon name="close" size={17} /></button></div>}
      <main className="message-scroll" ref={scroller} onScroll={() => { const area = scroller.current; nearBottom.current = area.scrollHeight - area.scrollTop - area.clientHeight < 90; setShowLatest(!nearBottom.current); }} aria-label="Messages" tabIndex="0">
        <div className="message-list">
          {!term && <div className="conversation-intro"><span className="intro-line" /><span>A little curiosity goes a long way.</span><span className="intro-line" /></div>}
          {loading && <div className="empty-state" role="status"><span className="loading-dot" /><p>Loading messages…</p></div>}
          {error && <div className="empty-state" role="alert"><Icon name="info" size={25} /><h2>We couldn’t load the conversation.</h2><p>Check your connection and try refreshing.</p></div>}
          {!loading && !error && !allMessages.length && <div className="empty-state"><Icon name="chat" size={30} /><h2>Every conversation starts somewhere.</h2><p>Say hello. Share an idea. See where it goes.</p></div>}
          {term && !visibleMessages.length && <div className="empty-state"><Icon name="search" size={28} /><h2>No messages found.</h2><p>Try another word or a person’s name.</p></div>}
          {visibleMessages.map((message, index) => {
            const date = messageDate(message);
            const previous = visibleMessages[index - 1];
            const previousDate = previous && messageDate(previous);
            const startsDay = date && (!previousDate || date.toDateString() !== previousDate.toDateString());
            const grouped = !startsDay && previous && previous.uid === message.uid && (!date || !previousDate || date - previousDate < 300000);
            return <Fragment key={message.id}>{startsDay && <div className="date-divider"><span>{dateLabel(date)}</span></div>}<ChatMessage message={message} user={user} grouped={grouped} /></Fragment>;
          })}
        </div>
      </main>
      {showLatest && !term && <button className="latest-button" onClick={scrollToLatest}><Icon name="down" size={16} />Latest messages</button>}
      <div className="composer-area">
        {sendError && <p className="composer-error" role="alert">{sendError}</p>}
        <form className="composer" onSubmit={sendMessage}>
          <label className="sr-only" htmlFor="message-input">Message</label>
          <textarea id="message-input" ref={input} rows="1" maxLength={4000} disabled={sending} value={formValue} onChange={e => setFormValue(e.target.value)} placeholder="Share a thought…" autoComplete="off" onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) sendMessage(e); }} />
          <button type="submit" className="send-button" aria-label="Send message" disabled={sending || !formValue.trim()}><Icon name="arrow" size={20} /></button>
        </form>
        <div className="composer-hint"><span>{sending ? <span role="status">Sending…</span> : 'A good conversation starts with an open mind.'}</span><span>↵ to send</span></div>
      </div>
    </section>
    {informationOpen && <RoomInformation onClose={() => setInformationOpen(false)} />}
  </div>;
}

function ChatMessage({ message, user, grouped }) {
  const sent = message.uid === user.uid;
  const date = messageDate(message);
  return <article className={`message ${sent ? 'sent' : 'received'}${grouped ? ' is-grouped' : ''}`} aria-label={`Message from ${sent ? 'you' : firstName(message.displayName)}`}>
    {!sent && <div className="message-avatar">{!grouped && <Avatar person={message} />}</div>}
    <div className="message-content">
      {!grouped && <div className="message-meta"><span>{sent ? 'You' : firstName(message.displayName)}</span>{date && <time dateTime={date.toISOString()}>{date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</time>}</div>}
      <p className="message-bubble">{message.text}</p>
    </div>
  </article>;
}

export default App;
